import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import { useScript } from '@client/hooks/useScript';

interface IGrecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
  render: (container: string | Element, parameters: any) => string;
  getResponse: (widgetId?: number) => string;
  reset: (widgetId?: number) => void;
}

interface IRecaptchaReturn {
  isRecaptchaLoaded: boolean;
  executeRecaptcha: (action?: string) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: IGrecaptcha;
  }
}

export const useRecaptcha = (siteKey: string): IRecaptchaReturn => {
  const { messageApi } = useContext(NotificationContext);
  const { t } = useTranslation();

  const { isLoaded: isRecaptchaLoaded } = useScript(
    `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
    !!window.grecaptcha,
    'reCAPTCHA',
  );

  const executeRecaptcha = useCallback(
    async (action: string = 'submit'): Promise<string> => {
      if (!isRecaptchaLoaded || !window.grecaptcha) {
        messageApi?.error(t('error.reCAPTCHA.loaded'));
        throw new Error(t('error.reCAPTCHA.loaded'));
      }

      return new Promise((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha?.execute(siteKey, { action });
            resolve(token);
          } catch (error) {
            reject(error);
          }
        });
      });
    },
    [isRecaptchaLoaded, messageApi, siteKey, t],
  );

  return { isRecaptchaLoaded, executeRecaptcha };
};
